import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FarmSettingsPatch, FarmSettingsRecord, NewFarmInput } from '@henlytics/types'
import { api } from '../lib/api-client'
import { queryKeys } from '../lib/query-keys'

/** The farm's own settings: name, currency, timezone, number format, feed unit, estimates. */
export function useFarmSettings(enabled = true) {
  return useQuery({
    queryKey: queryKeys.farm,
    queryFn: ({ signal }) => api.get<FarmSettingsRecord>('/farms/current', signal),
    enabled,
  })
}

export function useUpdateFarmSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: FarmSettingsPatch) => api.patch<FarmSettingsRecord>('/farms/current', patch),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.farm, settings)
      // Layer start week and the estimates feed the cost figures and layer dates.
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
      void queryClient.invalidateQueries({ queryKey: queryKeys.batches })
    },
  })
}

/** First-run setup: creates the farm and makes the signed-in user its owner. */
export function useCreateFarm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NewFarmInput) => api.post<FarmSettingsRecord>('/farms', input),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.farm, settings)
      void queryClient.invalidateQueries({ queryKey: queryKeys.session })
    },
  })
}
